import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { env } from '@/env';
import { BrandMark } from '@/components/Layout';

/// Sprint 28 — Google OAuth web sign-in for the admin dashboard.
///
/// The backend's email/password login was disabled in Sprint 25 to
/// remove a class of timing attacks; this dashboard followed suit.
/// Flow:
///   1. User clicks "Sign in with Google" here.
///   2. We redirect to /api/v1/auth/oauth/google/start with a `webRedirect`
///      pointing at /auth/callback on this dashboard.
///   3. Backend redirects browser to Google.
///   4. Google sends user back to backend's callback.
///   5. Backend redirects to /auth/callback#accessToken=...&refreshToken=...
///      (fragment so tokens never hit server logs).
///   6. /auth/callback reads the fragment and stores tokens.
///
/// Only Google is enabled — the other providers (GitHub, Bitbucket,
/// LinkedIn) work for the mobile app but the admin track is Google-first
/// since that's what most internal accounts use.
export default function LoginPage() {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const from =
    (location.state as { from?: { pathname?: string } } | undefined)?.from
      ?.pathname || '/';

  async function signInWithGoogle() {
    setError(null);
    setLoading(true);
    try {
      const callbackUrl = `${window.location.origin}/auth/callback`;
      // Persist the post-login destination so the callback can route back
      // to where the user originally tried to go.
      sessionStorage.setItem('admin.postLoginRedirect', from);
      const startUrl =
        `${env.apiBaseUrl}/api/v1/auth/oauth/google/start` +
        `?webRedirect=${encodeURIComponent(callbackUrl)}`;
      // Hit the JSON endpoint to get the actual Google authorize URL —
      // the backend mints a fresh state token tied to webRedirect.
      const res = await fetch(startUrl);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || `start failed (${res.status})`);
      }
      const json = await res.json();
      const url = json?.data?.url;
      if (typeof url !== 'string') throw new Error('No auth URL returned');
      // Top-level navigation so Google's CSP/X-Frame headers don't fight us.
      window.location.assign(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed.');
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <BrandMark size={56} />
          <h1 className="text-xl font-semibold tracking-tight">OffCoder Admin</h1>
          <p className="text-sm text-text-secondary">
            Sign in with an admin Google account.
          </p>
        </div>

        <div className="card space-y-4 p-6">
          <button
            onClick={signInWithGoogle}
            disabled={loading}
            className="btn-primary w-full"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <GoogleGlyph />
                Sign in with Google
              </>
            )}
          </button>

          {error && (
            <div className="rounded-lg border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
              {error}
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-text-tertiary">
          Need access? Kindly contact OffCoder support.
        </p>
      </div>
    </div>
  );
}

/// Tiny inline Google "G" glyph — saves an SVG asset import and keeps
/// the bundle a few KB smaller. Color-balanced for both light + dark.
function GoogleGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.49h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.92c1.71-1.57 2.7-3.89 2.7-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A8.99 8.99 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.71a5.4 5.4 0 0 1 0-3.43V4.96H.96a9 9 0 0 0 0 8.08l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8.96 8.96 0 0 0 9 0 8.99 8.99 0 0 0 .96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}
